import { ArtifactsRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { logger } from '../utils/logger';

/**
 * Handle unified artifacts management request
 * Supports operations: create, update, delete, getAll, getOne
 * Routes requests to backend collections
 */
export async function handleArtifactsRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      throw new Error(`Collection operation ${collection}.${op} not found`);
    }
    return await handler(params);
  };

  try {
    const request = ArtifactsRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;

    logger.info('[SDK-NODEJS] Received artifacts request:', JSON.stringify({ operation, requestData }, null, 2));

    const artifactId = requestData?.id;
    const name = requestData?.name;
    const createdBy = requestData?.createdBy;
    const dsl = requestData?.dsl;
    const type = requestData?.type;
    const filters = requestData?.filters;
    const status = requestData?.status;
    const deleted = requestData?.deleted;
    const limit = requestData?.limit;
    const offset = requestData?.offset;
    const sort = requestData?.sort;

    logger.info('[SDK-NODEJS] Extracted params:', JSON.stringify({
      name,
      type,
      filters: filters ? 'present' : 'undefined'
    }, null, 2));

    logger.info('[SDK-NODEJS] Full requestData.filters:', JSON.stringify(filters, null, 2));

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, name, createdBy, dsl, type, filters, status, executeCollection, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, artifactId, name, dsl, type, filters, status, deleted, executeCollection, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, artifactId, executeCollection, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, limit, executeCollection, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, artifactId, executeCollection, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, { filters, limit, offset, sort }, executeCollection, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle artifacts request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create artifact operation
 */
async function handleCreate(
  id: string,
  name: string | undefined,
  createdBy: string | undefined,
  dsl: Record<string, any> | undefined,
  type: string | undefined,
  filters: Record<string, any> | undefined,
  status: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!name) {
    sendResponse(id, {
      success: false,
      error: 'name is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('artifacts', 'create', {
      name,
      createdBy,
      dsl,
      type,
      filters,
      status
    });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Artifact created successfully'
    }, sendMessage, clientId);

    logger.info(`Artifact created: ID ${result.data.id}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create artifact'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update artifact operation
 */
async function handleUpdate(
  id: string,
  artifactId: number | undefined,
  name: string | undefined,
  dsl: Record<string, any> | undefined,
  type: string | undefined,
  filters: Record<string, any> | undefined,
  status: string | undefined,
  deleted: boolean | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!artifactId) {
    sendResponse(id, {
      success: false,
      error: 'Artifact ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('artifacts', 'update', { id: artifactId, name, dsl, type, filters, status, deleted });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Artifact updated successfully'
    }, sendMessage, clientId);

    logger.info(`Artifact updated: ID ${artifactId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update artifact'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete artifact operation
 */
async function handleDelete(
  id: string,
  artifactId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!artifactId) {
    sendResponse(id, {
      success: false,
      error: 'Artifact ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('artifacts', 'delete', { id: artifactId });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Artifact deleted successfully'
    }, sendMessage, clientId);

    logger.info(`Artifact deleted: ID ${artifactId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete artifact'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all artifacts operation
 */
async function handleGetAll(
  id: string,
  limit: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('artifacts', 'getAll', { limit });

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Retrieved ${result.count} artifacts`
    }, sendMessage, clientId);

    logger.info(`Retrieved all artifacts (count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get artifacts'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get one artifact operation
 */
async function handleGetOne(
  id: string,
  artifactId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!artifactId) {
    sendResponse(id, {
      success: false,
      error: 'Artifact ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('artifacts', 'getOne', { id: artifactId });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: `Retrieved artifact ID ${artifactId}`
    }, sendMessage, clientId);

    logger.info(`Retrieved artifact: ID ${artifactId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get artifact'
    }, sendMessage, clientId);
  }
}

/**
 * Handle query artifacts operation
 */
async function handleQuery(
  id: string,
  queryParams: {
    filters?: {
      createdBy?: string;
      type?: string;
      status?: string;
      name?: string;
      deleted?: boolean;
      createdAt?: string;
    };
    limit?: number;
    offset?: number;
    sort?: 'ASC' | 'DESC';
  },
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    logger.info('[SDK-NODEJS] handleQuery - queryParams:', JSON.stringify(queryParams, null, 2));
    logger.info('[SDK-NODEJS] handleQuery - filters.type:', queryParams.filters?.type);

    const result = await executeCollection('artifacts', 'query', {
      filters: queryParams.filters,
      limit: queryParams.limit || 50,
      offset: queryParams.offset || 0,
      sort: queryParams.sort || 'DESC',
    });

    logger.info('[SDK-NODEJS] handleQuery - executeCollection result count:', result.count);

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Query returned ${result.count} artifacts`
    }, sendMessage, clientId);

    logger.info(`Query artifacts (count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to query artifacts'
    }, sendMessage, clientId);
  }
}

/**
 * Send artifacts response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any; count?: number; message?: string },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'ARTIFACTS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'user',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
